import { axios } from './axios.js';

/**
 * Get HubSpot OAuth authorization URL
 * @param {Object} options - Options
 * @param {boolean} options.popup - Whether to open in popup mode
 */
export async function getHubSpotAuthUrl(options = {}) {
  try {
    const params = options.popup ? { popup: 'true' } : {};
    const response = await axios.get('/integrations/hubspot/auth-url', {
      params,
    });
    return response.data;
  } catch (error) {
    console.error('Failed to get HubSpot auth URL:', error);
    throw error;
  }
}

/**
 * Open HubSpot OAuth in a popup window
 * @returns {Promise<{success: boolean, portalId?: string, portalName?: string, error?: string}>}
 */
export function openHubSpotOAuthPopup() {
  return new Promise(async (resolve, reject) => {
    try {
      // Get the auth URL with popup mode
      const { authUrl } = await getHubSpotAuthUrl({ popup: true });

      // Calculate popup position (center of screen)
      const width = 500;
      const height = 600;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;

      // Open popup
      const popup = window.open(
        authUrl,
        'hubspot-oauth',
        `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no,scrollbars=yes,resizable=yes`,
      );

      if (!popup) {
        reject(new Error('Popup blocked. Please allow popups for this site.'));
        return;
      }

      // Listen for message from popup
      const messageHandler = event => {
        // Verify the message is from our popup
        if (event.data && event.data.type === 'HUBSPOT_OAUTH_SUCCESS') {
          window.removeEventListener('message', messageHandler);
          resolve({
            success: true,
            portalId: event.data.portalId,
            portalName: event.data.portalName,
          });
        } else if (event.data && event.data.type === 'HUBSPOT_OAUTH_ERROR') {
          window.removeEventListener('message', messageHandler);
          resolve({ success: false, error: event.data.error });
        }
      };

      window.addEventListener('message', messageHandler);

      // Also check if popup is closed without completing OAuth
      const checkClosed = setInterval(() => {
        if (popup.closed) {
          clearInterval(checkClosed);
          window.removeEventListener('message', messageHandler);
          // Give a small delay to allow any pending messages
          setTimeout(() => {
            resolve({
              success: false,
              error: 'Popup closed without completing authentication',
            });
          }, 500);
        }
      }, 500);
    } catch (error) {
      reject(error);
    }
  });
}

/**
 * Disconnect HubSpot account
 */
export async function disconnectHubSpotAccount() {
  try {
    const response = await axios.post('/integrations/hubspot/disconnect');
    return response.data;
  } catch (error) {
    console.error('Failed to disconnect HubSpot account:', error);
    throw error;
  }
}

/**
 * Get HubSpot account connection status
 */
export async function getHubSpotStatus() {
  try {
    const response = await axios.get('/integrations/hubspot/status');
    return response.data;
  } catch (error) {
    console.error('Failed to get HubSpot status:', error);
    throw error;
  }
}

/**
 * List available HubSpot portals
 */
export async function listHubSpotPortals() {
  try {
    const response = await axios.get('/integrations/hubspot/portals');
    return response.data;
  } catch (error) {
    console.error('Failed to list HubSpot portals:', error);
    throw error;
  }
}

/**
 * Select a HubSpot portal
 */
export async function selectHubSpotPortal(portalId) {
  try {
    const response = await axios.post('/integrations/hubspot/select-portal', {
      portalId,
    });
    return response.data;
  } catch (error) {
    console.error('Failed to select HubSpot portal:', error);
    throw error;
  }
}

/**
 * Get HubSpot schema for contacts
 */
export async function getHubSpotSchema() {
  try {
    const response = await axios.get('/integrations/hubspot/schema/contacts');
    return response.data;
  } catch (error) {
    console.error('Failed to get HubSpot schema:', error);
    throw error;
  }
}

/**
 * Get project HubSpot sync configuration
 * @param {number} projectId - Project ID
 */
export async function getProjectHubSpotConfig(projectId) {
  try {
    const response = await axios.get(
      `/integrations/hubspot/projects/${projectId}/config`,
    );
    return response.data;
  } catch (error) {
    console.error('Failed to get project HubSpot config:', error);
    throw error;
  }
}

/**
 * Update project HubSpot sync configuration
 * @param {number} projectId - Project ID
 * @param {Object} config - HubSpot sync configuration
 */
export async function updateProjectHubSpotConfig(projectId, config) {
  try {
    const response = await axios.put(
      `/integrations/hubspot/projects/${projectId}/config`,
      config,
    );
    return response.data;
  } catch (error) {
    console.error('Failed to update project HubSpot config:', error);
    throw error;
  }
}

/**
 * Trigger manual sync for a project (imports contacts from HubSpot)
 * @param {number} projectId - Project ID
 */
export async function triggerProjectSync(projectId) {
  try {
    const response = await axios.post(
      `/integrations/hubspot/projects/${projectId}/sync`,
    );
    return response.data;
  } catch (error) {
    console.error('Failed to trigger project sync:', error);
    throw error;
  }
}
